import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export interface NoticeAttachment {
  id: string;
  originalname: string;
  filename: string;
  mimetype?: string;
}

export interface Notice {
  id?: number;
  associationId?: number;
  title: string;
  content: string;
  status?: "draft" | "sent";
  targetRoles?: string[];
  attachments?: NoticeAttachment[];
  attachmentIds?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export type SendNoticeResponse = {
  batchId: string;
  recipients: number;
  streamUrl: string;
};

interface NoticeState {
  notices: Notice[];
  notice: Notice | null;
  open: boolean;
  status: "idle" | "loading" | "failed";
  error: string | null;
}

const initialState: NoticeState = {
  notices: [],
  notice: null,
  open: false,
  status: "idle",
  error: null,
};

export const fetchNotices = createAsyncThunk<Notice[], number>(
  "notice/fetchNotices",
  async (associationId) => {
    const response = await axiosInstance.get("/notices", {
      params: { associationId },
    });
    return response.data;
  }
);

export const createNotice = createAsyncThunk<Notice, any>(
  "notice/createNotice",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/notices", data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updateNotice = createAsyncThunk<
  Notice,
  { id: number; data: Partial<Notice> & { attachmentIds?: string[] } }
>("notice/updateNotice", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put(`/notices/${id}`, data);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message);
  }
});

export const deleteNotice = createAsyncThunk<number, number>(
  "notice/deleteNotice",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/notices/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const sendNotice = createAsyncThunk<
  SendNoticeResponse,
  { associationId: number; noticeId: number },
  { rejectValue: string }
>("notice/sendNotice", async (body, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance.post<SendNoticeResponse>(
      "/notices/send",
      body
    );
    return data;
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ?? err?.message ?? "Failed to send notice.";
    return rejectWithValue(msg);
  }
});

const noticeSlice = createSlice({
  name: "notice",
  initialState,
  reducers: {
    setNotice(state, action: PayloadAction<Notice | null>) {
      state.notice = action.payload;
    },
    openNoticeForm(state, action: PayloadAction<boolean>) {
      state.open = action.payload;
      if (!action.payload) {
        state.notice = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotices.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchNotices.fulfilled, (state, action) => {
        state.status = "idle";
        state.notices = action.payload;
      })
      .addCase(fetchNotices.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(createNotice.fulfilled, (state, action) => {
        state.notices.unshift(action.payload);
      })
      .addCase(updateNotice.fulfilled, (state, action) => {
        const idx = state.notices.findIndex((n) => n.id === action.payload.id);
        if (idx !== -1) state.notices[idx] = action.payload;
      })
      .addCase(deleteNotice.fulfilled, (state, action) => {
        state.notices = state.notices.filter((n) => n.id !== action.payload);
      })
      .addCase(sendNotice.fulfilled, (state, action) => {
        const { noticeId } = action.meta.arg;
        const idx = state.notices.findIndex((n) => n.id === noticeId);
        if (idx !== -1)
          state.notices[idx] = { ...state.notices[idx], status: "sent" };
      });
  },
});

export const { setNotice, openNoticeForm } = noticeSlice.actions;
export default noticeSlice.reducer;
